/**
 * Générateur — Automatisme : écrire un même nombre sous de multiples formes,
 * par exemple dire que 1,2 = 12/10 = 6/5 = 1 + 1/5 = 120 % = 120/100 etc.
 * Attendu ID : 1057 | Niveau : 5ème | Domaine : Nombres rationnels
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 8, ligne 571).
 * Cadrage rédigé par le sous-agent programme-maths, arbitré par le prof à
 * l'épreuve de Musk le 2026-09-09.
 *
 * Ce que 1057 ajoute au parc (épreuve de Musk du cadrage : quatre maillons de
 * la chaîne sont déjà des gens, 1048, 1051, 1053, 1054) : le POURCENTAGE
 * comme écriture d'un nombre, « sur 100 » pour pivot, dans les deux sens, y
 * compris au-delà de 100 % (145 % = 1,45) et sous 10 % (7 % = 0,07), et le
 * pont fraction simple → pourcentage par /100 (3/4 = 75/100 = 75 %).
 *
 * Plan de cartes validé, deux cartes :
 *   - Série flash       (4 lignes, une par conversion : 145 % = ⬚ ; 0,7 = ⬚ % ;
 *                        1,45 = ⬚/100 ; 3/4 = ⬚ %)
 *   - Le même nombre    (QCM « Quelle écriture est égale à 1,45 ? » 4 choix
 *                        en registres mêlés, sousType = registre de départ ;
 *                        Vrai/Faux 3 égalités entre deux registres)
 * Supprimés à l'épreuve de Musk : la carte « chaîne complète » (union des
 * voisins), Relier écritures, QCM intrus, nombre mixte et fraction simplifiée
 * (1053, 1051), dénominateurs 20/25/50, grandeurs, 100 % et 200 %.
 *
 * ── Domaine ──────────────────────────────────────────────────────────────
 * Un nombre = un entier de CENTIÈMES c de 1 à 199, jamais 100 ni 200 (1048),
 * jamais 120 : 1,2 est SACRIFIÉ sous toutes ses formes (120 %, 120/100,
 * 12/10, 6/5, 1 + 1/5), auto-testé. Quatre registres : virgule (1,45),
 * pourcentage (145 %), sur 100 (145/100), fraction simple de dénominateur 2,
 * 4 ou 5 quand elle existe (13 fractions). Trois zones : c < 10 (le rang),
 * 10 à 99, 100 à 199 (le passage de l'unité, discriminant). Nombre nu.
 *
 * ── Distracteurs (familles nommées, valeurs en centièmes) ────────────────
 *   lu_tel_quel          : les chiffres recopiés, le rang ignoré (1,2 = 12 % ;
 *                          12 % = 1,2 ; 3/4 = 34 %) — L'ERREUR PRINCIPALE
 *                          (DEPP 6e 2025 q.4, 28 %), ne se distingue de la clé
 *                          que sur une décimale ;
 *   rang                 : le bon geste au mauvais rang (7 % = 0,7 ;
 *                          0,05 = 50 % ; 145 % = 14,5), nom de 1048 ;
 *   pourcentage_recopie  : le % ne change rien (145 % = 145), nom de 1056 ;
 *   denominateur_recopie : 1/4 = 4 %, fractions unitaires ;
 *   nombres_accoles      : entier et décimales lus séparément (1,2 = 102 %),
 *                          secondaire, une fois par tableau, jamais seule.
 * Chaque distracteur : valeur > 0, différente de la clé, écrite dans le
 * registre cible. Aucune famille imposée sur une fausse (leçon 1054).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
} from '../../../utils';

const ATTENDU_ID = 1057;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_MEME = 'Le même nombre';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche, sacrifié : 1,2 sous toutes ses formes. */
export const SACRIFIE = 120;
const C_MAX = 199;
const EXCLUS = [SACRIFIE, 100];

/** Les fractions simples (dénominateur 2, 4, 5), irréductibles, < 2, non entières, hors 6/5. */
const FRACTIONS = [];
for (const b of [2, 4, 5]) {
    for (let a = 1; a < 2 * b; a++) {
        const c = (a * 100) / b;
        if (a % b !== 0 && (b !== 4 || a % 2 !== 0) && !EXCLUS.includes(c)) FRACTIONS.push({ a, b, c });
    }
}
const fractionDe = (c) => FRACTIONS.find((f) => f.c === c) || null;

/** Centièmes → écriture KaTeX à virgule (145 → « 1{,}45 », 70 → « 0{,}7 », 5 → « 0{,}05 »). */
const virguleTex = (c) => {
    const e = Math.floor(c / 100);
    const r = c % 100;
    return r === 0 ? String(e) : `${e}{,}${String(r).padStart(2, '0').replace(/0$/, '')}`;
};
/** Centièmes → écriture à virgule en texte brut (« 1,45 »), pour les trous de saisie. */
const virguleTexte = (c) => virguleTex(c).replace('{,}', ',');

const REGISTRES = ['virgule', 'pct', 'sur100', 'simple'];
/** L'écriture KaTeX (sans $) de c dans un registre ; null si la fraction simple n'existe pas. */
const ecriture = (registre, c) => {
    if (registre === 'virgule') return virguleTex(c);
    if (registre === 'pct') return `${c}\\,\\%`;
    if (registre === 'sur100') return `\\dfrac{${c}}{100}`;
    const f = fractionDe(c);
    return f ? `\\dfrac{${f.a}}{${f.b}}` : null;
};

const valide = (c) => Number.isInteger(c) && c >= 1 && c <= C_MAX && !EXCLUS.includes(c);

/** Un nombre au hasard : une fois sur trois une seule décimale, une fois sur trois au-delà de 1, sinon libre. */
const tirerC = (exclus = []) => {
    for (let essai = 0; essai < 200; essai++) {
        const forme = randomChoice(['decimale', 'unite', 'libre']);
        const c = forme === 'decimale' ? 10 * randInt(1, 19) : (forme === 'unite' ? randInt(101, C_MAX) : randInt(1, C_MAX));
        if (valide(c) && !exclus.includes(c)) return c;
    }
    return null;
};

const EXPLICATIONS = {
    lu_tel_quel: 'Pour cent, c\'est sur 100 : 1,2 = 120/100 = 120 %, pas 12 %. Je regarde le rang des chiffres, pas seulement les chiffres.',
    rang: '7 % = 7/100 = 0,07 : deux rangs après la virgule, pas un.',
    pourcentage_recopie: 'Le signe % veut dire « sur 100 » : 145 %, c\'est 145/100 = 1,45, pas 145.',
    denominateur_recopie: '1/4, c\'est 25/100 = 25 % : je cherche combien de centièmes, pas le dénominateur.',
    nombres_accoles: '1,2 = 1 + 2/10 = 100/100 + 20/100 = 120 % : les dixièmes font 10 centièmes chacun.',
    regle: 'Pour cent, c\'est sur 100 : 145 % = 145/100 = 1,45 ; 3/4 = 75/100 = 75 %.',
};

// ─── Distracteurs ─────────────────────────────────────────────────────────

/** Les chiffres d'une écriture à virgule, accolés (1,45 → 145 ; 0,7 → 7). */
const chiffres = (c) => Number(virguleTexte(c).replace(',', '').replace(/^0+(?=\d)/, ''));

/**
 * Tous les candidats (valeurs en centièmes) pour un nombre c lu dans le
 * registre `depart`, à écrire dans le registre `cible`.
 */
const tousCandidats = (c, depart) => {
    const out = [];
    // jamais 120 non plus : 1,2 sacrifié ne sort même pas comme distracteur (12 % → × 10)
    const pousser = (code, val) => { if (Number.isInteger(val) && val > 0 && val !== c && val !== SACRIFIE && val <= 100000) out.push({ code, val }); };
    if (depart === 'virgule') {
        pousser('lu_tel_quel', chiffres(c)); // 1,2 → 12 (%) ; 1,45 → 145 = clé, filtré
        pousser('nombres_accoles', Math.floor(c / 100) * 100 + Number(String(c % 100).replace(/0$/, ''))); // 1,2 → « 1 et 2 » → 102 ; 1,45 → 145 = clé, filtré
        // pourcentage_recopie (1,45 → 1,45 %) n'est pas un pourcentage entier : jamais ici
    } else if (depart === 'pct') {
        const s = String(c);
        if (s.length === 2) pousser('lu_tel_quel', c * 10); // 12 % → 1,2
        pousser('pourcentage_recopie', c * 100); // 145 % → 145
        if (c % 100 < 10 && c > 100) pousser('nombres_accoles', Math.floor(c / 100) * 100 + (c % 100) * 10); // 102 % → 1,2
    } else {
        const f = fractionDe(c);
        if (f) {
            pousser('lu_tel_quel', Number(`${f.a}${f.b}`)); // 3/4 → 34 %
            if (f.a === 1) pousser('denominateur_recopie', f.b); // 1/4 → 4 %
        }
    }
    pousser('rang', c * 10);
    if (c % 10 === 0) pousser('rang', c / 10);
    const vus = new Set();
    return out.filter((x) => { if (vus.has(x.val)) return false; vus.add(x.val); return true; });
};

/** Une variante au hasard par famille (Vrai/Faux). */
const candidats = (c, depart) => {
    const parCode = new Map();
    for (const x of shuffle(tousCandidats(c, depart))) if (!parCode.has(x.code)) parCode.set(x.code, x);
    return [...parCode.values()];
};

/**
 * QCM : trois candidats de valeurs distinctes, familles distinctes quand les
 * conceptions le permettent (deux ou trois familles seulement selon le
 * registre de départ : rang porte alors deux valeurs, × 10 et ÷ 10). Pas de
 * sélection par rang de la clé : les quatre choix sont écrits dans des
 * registres MÊLÉS (revue 2026-09-09 : dans un même registre, la clé était le
 * 3e nombre 61 % du temps, presque toutes les conceptions passant dessous),
 * si bien que trier les choix par valeur exige de les convertir, la
 * compétence testée elle-même.
 */
const distracteursQcm = (c, depart) => {
    const cands = tousCandidats(c, depart);
    const triples = [];
    for (let i = 0; i < cands.length; i++) {
        for (let j = i + 1; j < cands.length; j++) {
            for (let k = j + 1; k < cands.length; k++) {
                const t = [cands[i], cands[j], cands[k]];
                triples.push({ t, familles: new Set(t.map((x) => x.code)).size });
            }
        }
    }
    if (!triples.length) return null;
    const max = Math.max(...triples.map((x) => x.familles));
    return randomChoice(triples.filter((x) => x.familles === max)).t;
};

// ─── Carte 1 : Série flash (4 lignes KaTeX, un trou chacune) ──────────────

const CONSIGNE_FLASH = '<p><b>Complète.</b></p>';

/** Une ligne par conversion ; le trou porte la valeur dans le registre cible. */
const ligneFlash = (it) => {
    if (it.type === 'virgule') return `${it.c}\\,\\% = {{${virguleTexte(it.c)}}}`;
    if (it.type === 'pct') return `${virguleTex(it.c)} = {{${it.c}}}\\,\\%`;
    if (it.type === 'sur100') return `${virguleTex(it.c)} = \\dfrac{{{${it.c}}}\\rule[-0.55em]{0pt}{0.55em}}{100}`;
    return `${ecriture('simple', it.c)} = {{${it.c}}}\\,\\%`;
};
const phraseFlash = (items) => [items.slice(0, 2), items.slice(2, 4)].map((paire) => `$${paire.map(ligneFlash).join(' \\quad ; \\quad ')}$`).join('\n');

const payloadFlash = (items) => {
    const payload = makeTexteTrou(phraseFlash(items), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle, lu_tel_quel: EXPLICATIONS.lu_tel_quel } };
    payload._meta.params = { items };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 200; essai++) {
        const types = shuffle([...REGISTRES]);
        const items = [];
        for (const type of types) {
            const c = type === 'simple' ? randomChoice(FRACTIONS.filter((f) => !items.some((it) => it.c === f.c))).c : tirerC(items.map((it) => it.c));
            if (!c) break;
            items.push({ type, c });
        }
        if (items.length < 4) continue;
        // un nombre au-delà de 1, un nombre à une seule décimale (là où « lire les chiffres » échoue), au plus un nombre < 10 %
        if (!items.some((it) => it.c > 100)) continue;
        if (!items.some((it) => it.c % 10 === 0 && it.c % 100 !== 0)) continue;
        if (items.filter((it) => it.c < 10).length > 1) continue;
        return payloadFlash(items);
    }
    // Repli déterministe vérifié : 145 % = 1,45 ; 0,7 = 70 % ; 1,35 = 135/100 ; 3/4 = 75 %.
    return payloadFlash([{ type: 'virgule', c: 145 }, { type: 'pct', c: 70 }, { type: 'sur100', c: 135 }, { type: 'simple', c: 75 }]);
};

// ─── Carte 2, format A : QCM, quatre écritures en registres mêlés ─────────

/** Un registre au hasard, différent du départ, où la valeur s'écrit (fraction simple seulement si elle existe). */
const registreDeChoix = (depart, val) => {
    const possibles = REGISTRES.filter((r) => r !== depart && ecriture(r, val));
    return possibles.length ? randomChoice(possibles) : null;
};

const genQcm = () => {
    for (let essai = 0; essai < 200; essai++) {
        const depart = randomChoice(['virgule', 'pct', 'simple']);
        const c = depart === 'simple' ? randomChoice(FRACTIONS).c : tirerC();
        if (!c) continue;
        const faux = distracteursQcm(c, depart);
        if (!faux) continue;
        const valeurs = [c, ...faux.map((f) => f.val)];
        const registres = valeurs.map((v) => registreDeChoix(depart, v));
        if (registres.some((r) => !r)) continue;
        const choix = valeurs.map((v, i) => `$${ecriture(registres[i], v)}$`);
        if (new Set(choix).size !== 4) continue;
        const texte = `<p><b>Quelle écriture est égale à ${tex(ecriture(depart, c))} ?</b></p>`;
        const payload = makeQcm(choix, choix[0], texte, opts(L_MEME));
        payload.contenu = { ...payload.contenu, sousType: depart, explications: EXPLICATIONS };
        payload._meta.params = { c, depart, registres, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli déterministe vérifié : 3/4 = 75 % ; lu_tel_quel 0,34, rang 750/100, denominateur_recopie 4 %.
    const c = 75;
    const bon = '$75\\,\\%$';
    const payload = makeQcm([bon, '$0{,}34$', '$\\dfrac{750}{100}$', '$4\\,\\%$'], bon, `<p><b>Quelle écriture est égale à ${tex('\\dfrac{3}{4}')} ?</b></p>`, opts(L_MEME));
    payload.contenu = { ...payload.contenu, sousType: 'simple', explications: EXPLICATIONS };
    payload._meta.params = { c, depart: 'simple', registres: ['pct', 'virgule', 'sur100', 'pct'], distracteurs: [{ code: 'lu_tel_quel' }, { code: 'rang' }, { code: 'denominateur_recopie' }] };
    return payload;
};

// ─── Carte 2, format B : Vrai ou faux (3 égalités entre deux registres) ───

/** « écriture(g, c) = écriture(d, c ou c') », vraie ou fausse. */
const affirmation = (c, vraie) => {
    const dispo = REGISTRES.filter((r) => ecriture(r, c));
    const [g, d] = shuffle(dispo).slice(0, 2);
    if (vraie) return { ligne: tex(`${ecriture(g, c)} = ${ecriture(d, c)}`), code: null };
    const cands = candidats(c, g === 'sur100' ? 'pct' : g);
    if (!cands.length) return null;
    const x = randomChoice(cands);
    const droite = ecriture(d === 'simple' ? 'pct' : d, x.val);
    if (!droite) return null;
    return { ligne: tex(`${ecriture(g, c)} = ${droite}`), code: x.code };
};

const genVf = () => {
    for (let essai = 0; essai < 200; essai++) {
        const cs = [];
        for (let k = 0; k < 3; k++) {
            const c = k === 0 ? randomChoice(FRACTIONS).c : tirerC(cs);
            if (!c || cs.includes(c)) break;
            cs.push(c);
        }
        if (cs.length < 3) continue;
        const nbVraies = randomChoice([1, 2]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(3 - nbVraies).fill(false)]);
        const affs = shuffle(cs).map((c, i) => affirmation(c, verites[i]));
        if (affs.some((a) => !a)) continue;
        const codes = affs.filter((a) => a.code).map((a) => a.code);
        const nbAcc = codes.filter((x) => x === 'nombres_accoles').length;
        if (nbAcc > 1 || (nbAcc === 1 && codes.length === 1)) continue;
        const lignes = affs.map((a) => a.ligne);
        if (new Set(lignes).size < 3) continue;
        const payload = makeVraiFaux(lignes, verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { cs, verites, distracteurs: codes.map((code) => ({ code })) };
        return payload;
    }
    // Repli déterministe : 3/2 = 150 % vrai ; 0,7 = 7/100 faux (lu_tel_quel) ; 145 % = 14,5 faux (rang).
    const lignes = [tex('\\dfrac{3}{2} = 150\\,\\%'), tex('0{,}7 = \\dfrac{7}{100}'), tex('145\\,\\% = 14{,}5')];
    const payload = makeVraiFaux(lignes, [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_MEME));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { cs: [150, 70, 145], verites: [true, false, false], distracteurs: [{ code: 'lu_tel_quel' }, { code: 'rang' }] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'c de 1 à 199 centièmes, jamais 100 ni 120 (1,2 sacrifié sous toutes ses formes) ; registres virgule, pourcentage, sur 100, fraction simple (dénominateurs 2, 4, 5) ; nombre nu',
        'chaque distracteur : valeur > 0, différente de la clé, écrite dans le registre cible ; aucune famille imposée sur une fausse',
        'chaque distracteur porte son code dans _meta.params.distracteurs',
    ],
    meme_qcm_unique: {
        support: '« Quelle écriture est égale à … ? », départ virgule, pourcentage ou fraction simple (contenu.sousType), les quatre choix en registres MÊLÉS tirés au hasard hors du départ (trier par valeur exige de convertir) ; trois candidats, familles distinctes quand possible ; mesuré à la revue sur 5 000 tirages : « le choix seul dans son registre » 19 %, « mêmes chiffres que l\'énoncé » 9 %, « toujours le registre X » 7 à 19 %, rien au-dessus du hasard ; le départ virgule ne sort qu\'avec une seule décimale (à deux décimales, lu_tel_quel et nombres_accoles tombent sur la clé et il ne reste pas trois candidats), soit 14 % des QCM',
        familles: {
            lu_tel_quel: 'les chiffres recopiés, le rang ignoré (1,2 = 12 % ; 12 % = 1,2 ; 3/4 = 34 %), l\'erreur principale',
            rang: 'le bon geste au mauvais rang (× 10 ou ÷ 10)',
            pourcentage_recopie: 'le % ne change rien (145 % = 145)',
            denominateur_recopie: '1/4 = 4 %, fractions unitaires',
            nombres_accoles: '1,2 = 102 %, secondaire',
        },
    },
    meme_tableau_vf: 'trois égalités entre deux registres, 1 ou 2 vraies, familles libres, nombres_accoles jamais seule',
    flash_texte_trou_saisie: 'quatre lignes, une par conversion (% → virgule, virgule → %, virgule → /100, fraction simple → %), un nombre > 1, un nombre à une décimale, au plus un nombre < 10 %',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    meme_qcm_unique: genQcm,
    meme_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'meme', label: L_MEME, formatsList: [
        { key: 'meme_qcm_unique', label: 'QCM' },
        { key: 'meme_tableau_vf', label: 'Vrai/Faux' },
    ] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
